import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Image, Plus, Pencil, Trash2, Loader2, Search, ImageIcon, Upload, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BannersPage = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    image: "",
    banner_url: "",
    is_enabled: true
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("gasman_token")}` }
  });

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners`, getAuthHeaders());
      setBanners(response.data);
    } catch (error) {
      toast.error("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("gasman_token")}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setFormData({ ...formData, image: response.data.url });
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    if (imagePath.startsWith("/uploads/")) {
      return `${BACKEND_URL}/api${imagePath}`;
    }
    return `${BACKEND_URL}${imagePath}`;
  };

  const resetForm = () => {
    setFormData({
      title: "",
      image: "",
      banner_url: "",
      is_enabled: true
    });
    setSelectedBanner(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (banner) => {
    setSelectedBanner(banner);
    setFormData({
      title: banner.title || "",
      image: banner.image || "",
      banner_url: banner.banner_url || "",
      is_enabled: banner.is_enabled
    });
    setImagePreview(banner.image ? getImageUrl(banner.image) : null);
    setDialogOpen(true);
  };

  const openDeleteDialog = (banner) => {
    setSelectedBanner(banner);
    setDeleteDialogOpen(true);
  };

  const handleToggleEnabled = async (banner) => {
    try {
      await axios.put(`${API}/banners/${banner.id}`, { is_enabled: !banner.is_enabled }, getAuthHeaders());
      toast.success(`Banner ${!banner.is_enabled ? 'enabled' : 'disabled'}`);
      fetchBanners();
    } catch (error) {
      toast.error("Failed to update banner");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image) {
      toast.error("Please upload an image");
      return;
    }
    
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        title: formData.title || null,
        banner_url: formData.banner_url || null
      };
      
      if (selectedBanner) {
        await axios.put(`${API}/banners/${selectedBanner.id}`, payload, getAuthHeaders());
        toast.success("Banner updated successfully");
      } else {
        await axios.post(`${API}/banners`, payload, getAuthHeaders());
        toast.success("Banner added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchBanners();
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/banners/${selectedBanner.id}`, getAuthHeaders());
      toast.success("Banner deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedBanner(null);
      fetchBanners();
    } catch (error) {
      toast.error("Failed to delete banner");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredBanners = banners.filter(b =>
    (b.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.banner_url || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="banners-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Image className="text-[#0284c7]" size={28} />
            Banners
          </h1>
          <p className="text-gray-600 mt-1">Manage promotional banners</p>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#6BB5E0] text-white hover:bg-[#38BDF8] rounded-lg glow-primary"
          data-testid="add-banner-btn"
        >
          <Plus size={20} className="mr-2" />
          Add Banner
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search banners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-gray-300"
            data-testid="search-banners"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#6BB5E0]" size={32} />
          </div>
        ) : filteredBanners.length === 0 ? (
          <div className="text-center py-12">
            <Image className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm ? "No banners found matching your search" : "No banners added yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700 w-32">Image</TableHead>
                  <TableHead className="font-bold text-gray-700">Title</TableHead>
                  <TableHead className="font-bold text-gray-700">Banner URL</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Enabled</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBanners.map((banner) => (
                  <TableRow key={banner.id} className="hover:bg-gray-50" data-testid={`banner-row-${banner.id}`}>
                    <TableCell>
                      {banner.image ? (
                        <img src={getImageUrl(banner.image)} alt={banner.title || "Banner"} className="w-24 h-14 rounded-lg object-cover" />
                      ) : (
                        <div className="w-24 h-14 rounded-lg bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="text-gray-400" size={20} />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-gray-900">
                      {banner.title || <span className="text-gray-400 italic">No title</span>}
                    </TableCell>
                    <TableCell>
                      {banner.banner_url ? (
                        <a 
                          href={banner.banner_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#0284c7] hover:underline flex items-center gap-1 max-w-xs truncate"
                        >
                          {banner.banner_url}
                          <ExternalLink size={14} />
                        </a>
                      ) : (
                        <span className="text-gray-400 italic">No URL</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={banner.is_enabled}
                        onCheckedChange={() => handleToggleEnabled(banner)}
                        data-testid={`toggle-banner-${banner.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(banner)}
                          className="text-gray-600 hover:text-[#0284c7]"
                          data-testid={`edit-banner-${banner.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(banner)}
                          className="text-gray-600 hover:text-red-500"
                          data-testid={`delete-banner-${banner.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {selectedBanner ? "Edit Banner" : "Add New Banner"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title" className="text-gray-700 font-medium">Title (Optional)</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Banner title"
                className="mt-1 bg-white border-gray-300"
                data-testid="input-banner-title"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Image <span className="text-red-500">*</span></Label>
              <div className="mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                  id="banner-image-upload"
                  data-testid="input-banner-image"
                />
                <div className="flex items-center gap-4">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-32 h-20 rounded-lg object-cover" />
                  ) : (
                    <div className="w-32 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="text-gray-400" size={24} />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="border-gray-300"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Upload Image
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="banner_url" className="text-gray-700 font-medium">Banner URL (Optional)</Label>
              <Input
                id="banner_url"
                name="banner_url"
                value={formData.banner_url}
                onChange={handleChange}
                placeholder="https://example.com/promo"
                className="mt-1 bg-white border-gray-300"
                data-testid="input-banner-url"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_enabled" className="text-gray-700 font-medium">Enabled</Label>
              <Switch
                id="is_enabled"
                checked={formData.is_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                data-testid="input-banner-enabled"
              />
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || uploading}
                className="bg-[#6BB5E0] text-white hover:bg-[#38BDF8]"
                data-testid="submit-banner-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {selectedBanner ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  selectedBanner ? "Update Banner" : "Add Banner"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Delete Banner</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Are you sure you want to delete this banner? This action cannot be undone.
          </p>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-gray-300 text-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={submitting}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannersPage;
